import { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

/**
 * Hook that overrides browser back button to navigate to a `return` query param path.
 * Use in assessment home pages so that after completing an assessment,
 * pressing back goes to the health span landing page instead of question pages.
 *
 * @param {Object} options
 * @param {boolean} [options.enabled=true] - Whether the override is active
 */
const useReturnNavigation = ({ enabled = true } = {}) => {
  const history = useHistory();
  const { search } = useLocation();

  useEffect(() => {
    if (!enabled) return;

    const params = new URLSearchParams(search);
    const returnPath = params.get('return');
    if (!returnPath) return;

    // Push a duplicate entry so that popstate fires when user clicks back
    window.history.pushState({ returnOverride: true }, '');

    const handlePopState = (event) => {
      if (event.state?.returnOverride) {
        // User pressed back again from the override state, let it go normally
        return;
      }
      // Navigate to return path, replacing current history entry
      history.replace(decodeURIComponent(returnPath));
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [enabled, search, history]);
};

export default useReturnNavigation;
